/**
 * Sistema de diseño Neocoop — para documentos imprimibles y vistas web.
 *
 * Extraído del comprobante v6 (NC-105/106) que Hugo aprobó como el
 * "lenguaje visual" oficial. Este archivo centraliza tokens y helpers
 * para que welcome cards, emails, imprimibles futuros y partes de la
 * app compartan la misma estética.
 *
 * Filosofía:
 *   1. El asociado es el protagonista, no un dato de tabla.
 *   2. Brand discreto pero presente — isotipo como pattern, no como ornamento.
 *   3. Tipografía hace el trabajo. Cero ornamentos legacy.
 *   4. Mínimo legal. Cero firmas que ya no se firman.
 *   5. Copy interactions con feedback verde sutil.
 *   6. Generoso con el aire entre bloques.
 *
 * Anti-patrones (lo que NO hacemos):
 *   - Gradientes dramáticos cubriendo cards enteras
 *   - Firmas de papel
 *   - Bubbles flotantes con checkmarks gigantes
 *   - Sombras pesadas grises
 *   - Bordes con guiones gritando
 *   - Mayúsculas agresivas en todo (solo en eyebrows discretos)
 */

// ============================================================
// TOKENS — el ADN visual
// ============================================================

export const NEOCOOP_TOKENS = {
  color: {
    brand:       '#FF5B04',     // naranja brand — accents, headers, valores importantes
    brandDeep:   '#c43c00',     // hover, focus, sombras tinted
    brandSoft:   '#ffe4d4',     // backgrounds suaves
    ink:         '#14171c',     // texto principal y monto huge
    inkSoft:     '#4b5563',     // texto secundario, descripciones
    muted:       '#9aa0a6',     // labels, eyebrows, metadata
    hairline:    '#ebebed',     // separadores, bordes muy sutiles
    bgPage:      '#e9eaec',     // fondo de página al ver imprimibles en pantalla
    surface:     '#ffffff',     // cards
    softGray:    '#f6f7f9',     // bloques internos sutiles (depósito detail, desglose)
    okBg:        '#d1fae5',     // pill de estado positivo
    okInk:       '#047857',     // texto de estado positivo
    okDot:       '#10b981',     // indicador live verde
  },
  font: {
    body:  `'Noto Sans', 'Inter', system-ui, -apple-system, Segoe UI, sans-serif`,
    mono:  `'IBM Plex Mono', 'JetBrains Mono', monospace`,
  },
  radius: {
    card:   '24px',
    inner:  '16px',
    chip:   '10px',
    pill:   '999px',
  },
  shadow: {
    card:    '0 30px 60px rgba(0,0,0,0.10), 0 4px 12px rgba(0,0,0,0.04)',
    hero:    '0 8px 24px rgba(255,91,4,0.10), 0 2px 6px rgba(0,0,0,0.04)',
  },
  pattern: {
    isotipoBlanco:  `/brand/isotipo-blanco.svg`,
    isotipoNaranja: `/brand/isotipo-naranja.svg`,
    logoHorizontal: `/brand/logo-horizontal.png`,
  },
  // Espaciado guía para imprimibles
  spacing: {
    cardPadding:    '36px 28px',
    bodyPadding:    '26px 28px 14px',
    heroPadding:    '22px 22px 18px',
  },
}

// ============================================================
// CSS RESET + TOKENS — base para todo imprimible
// ============================================================

export function printableBaseCss(): string {
  const t = NEOCOOP_TOKENS
  return `
:root {
  --brand: ${t.color.brand};
  --brand-deep: ${t.color.brandDeep};
  --brand-soft: ${t.color.brandSoft};
  --ink: ${t.color.ink};
  --ink-soft: ${t.color.inkSoft};
  --muted: ${t.color.muted};
  --hairline: ${t.color.hairline};
  --bg-page: ${t.color.bgPage};
  --surface: ${t.color.surface};
  --soft-gray: ${t.color.softGray};
  --ok-bg: ${t.color.okBg};
  --ok-ink: ${t.color.okInk};
  --ok-dot: ${t.color.okDot};
}
* { box-sizing: border-box; }
html, body { margin: 0; padding: 0; }
body {
  font-family: ${t.font.body};
  background: var(--bg-page);
  min-height: 100vh;
  display: flex; align-items: flex-start; justify-content: center;
  padding: 32px 16px;
  color: var(--ink);
  -webkit-font-smoothing: antialiased;
  -webkit-print-color-adjust: exact;
  print-color-adjust: exact;
}
@media print {
  body { background: #fff; padding: 0; min-height: 0; align-items: stretch; }
  .toolbar { display: none !important; }
}
`.trim()
}

// ============================================================
// CSS de TOOLBAR — botón "Imprimir / PDF" estándar
// ============================================================

export function printableToolbarCss(width = 388): string {
  return `
.toolbar { width: ${width}px; margin: 0 auto 12px; display: flex; gap: 6px; justify-content: flex-end; align-items: center; }
.toolbar .hint {
  font-size: 11px; color: var(--muted); margin-right: auto;
}
.toolbar button {
  background: var(--surface); color: var(--ink);
  border: 1px solid var(--hairline);
  padding: 8px 14px; font-size: 12px; font-weight: 600;
  border-radius: 999px; cursor: pointer; font-family: inherit;
  letter-spacing: 0.2px;
  transition: all 120ms ease;
  display: inline-flex; align-items: center; gap: 6px;
}
.toolbar button:hover { border-color: var(--ink); }
.toolbar button.primary {
  background: var(--brand); color: #fff; border-color: var(--brand);
}
.toolbar button.primary:hover { background: var(--brand-deep); border-color: var(--brand-deep); }
.toolbar button:disabled { opacity: 0.5; cursor: wait; }
`.trim()
}

/**
 * Script que agrega capacidad de descargar el contenido como PNG.
 * Usa html2canvas desde CDN (sin agregar deps al bundle del admin).
 *
 * Espera que exista un elemento con `id="downloadable-card"` en el body —
 * eso es lo que se captura. El botón debe tener `data-action="download-png"`.
 *
 * Param: filename — nombre del archivo descargado.
 */
export function printableDownloadScript(filename: string): string {
  return `
(function() {
  var btn = document.querySelector('[data-action="download-png"]');
  if (!btn) return;

  /**
   * Cargamos html-to-image desde CDN (sucesora moderna de html2canvas).
   * Soporta CSS3 completa: gradients, transforms rotados, pseudo-elementos,
   * box-shadow, backdrop-filter. html2canvas crasheaba con "Cannot read 'type'"
   * en los gradients del sello diagonal.
   * UMD expone \`window.htmlToImage\` con API .toPng(node, options).
   */
  function loadHtmlToImage() {
    if (window.htmlToImage) return Promise.resolve(window.htmlToImage);
    return new Promise(function(resolve, reject) {
      var s = document.createElement('script');
      s.src = 'https://cdn.jsdelivr.net/npm/html-to-image@1.11.13/dist/html-to-image.js';
      s.crossOrigin = 'anonymous';
      s.onload = function() {
        if (window.htmlToImage) resolve(window.htmlToImage);
        else reject(new Error('html-to-image cargó pero no expuso el global window.htmlToImage'));
      };
      s.onerror = function() { reject(new Error('No se pudo cargar html-to-image desde CDN. Revisá tu conexión a internet.')); };
      document.head.appendChild(s);
    });
  }

  /**
   * Pre-fetch de imágenes externas a data URL — evita problemas CORS
   * cuando el server remoto del QR no manda Access-Control-Allow-Origin.
   */
  function imageToDataUrl(url) {
    return fetch(url, { mode: 'cors' })
      .then(function(r) { return r.blob(); })
      .then(function(blob) {
        return new Promise(function(resolve, reject) {
          var reader = new FileReader();
          reader.onloadend = function() { resolve(reader.result); };
          reader.onerror = reject;
          reader.readAsDataURL(blob);
        });
      });
  }

  function inlineExternalImages(root) {
    var imgs = root.querySelectorAll('img');
    var promises = [];
    for (var i = 0; i < imgs.length; i++) {
      (function(img) {
        var src = img.getAttribute('src') || '';
        if (!src || src.indexOf('data:') === 0) return;
        if (!/^https?:/.test(src)) return;
        promises.push(
          imageToDataUrl(src)
            .then(function(dataUrl) { img.setAttribute('src', dataUrl); })
            .catch(function(e) {
              console.warn('No pude inlinear imagen', src, e);
              img.remove();
            })
        );
      })(imgs[i]);
    }
    return Promise.all(promises);
  }

  btn.addEventListener('click', async function() {
    var card = document.getElementById('downloadable-card');
    if (!card) return;
    btn.disabled = true;
    var originalLabel = btn.querySelector('.btn-label');
    var oldText = originalLabel ? originalLabel.textContent : btn.textContent;
    if (originalLabel) originalLabel.textContent = 'Procesando…';
    else btn.textContent = 'Procesando…';

    try {
      // 1) Pre-cargar imágenes externas como data URL (resuelve CORS del QR)
      await inlineExternalImages(card);
      // 2) Cargar lib + capturar
      var hti = await loadHtmlToImage();
      var rect = card.getBoundingClientRect();
      // pixelRatio=2 para crisp Retina/HD al compartir por WhatsApp
      var dataUrl = await hti.toPng(card, {
        pixelRatio: 2,
        backgroundColor: '#ffffff',
        width: rect.width,
        height: rect.height,
        cacheBust: true,
      });
      var a = document.createElement('a');
      a.href = dataUrl;
      a.download = ${JSON.stringify(filename)};
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
    } catch (err) {
      console.error('Error generando PNG:', err);
      var msg = (err && err.message) ? err.message : String(err);
      alert('No se pudo generar la imagen: ' + msg + '\\n\\nProbá imprimir como PDF en su lugar.');
    } finally {
      btn.disabled = false;
      if (originalLabel) originalLabel.textContent = oldText;
      else btn.textContent = oldText;
    }
  });
})();
`.trim()
}

// ============================================================
// CSS para CARD CONTENEDORA — la forma estándar
// ============================================================

export function printableCardCss(width = 388): string {
  const t = NEOCOOP_TOKENS
  return `
.card-neocoop {
  width: ${width}px;
  background: var(--surface);
  border-radius: ${t.radius.card};
  overflow: hidden;
  box-shadow: ${t.shadow.card};
}
@media print {
  .card-neocoop { box-shadow: none; border-radius: 0; }
}
`.trim()
}

// ============================================================
// CSS para HEADER BRAND con isotipo pattern wallpaper
// ============================================================

export function printableHeaderCss(): string {
  const t = NEOCOOP_TOKENS
  return `
.neo-header {
  background: var(--brand);
  color: #fff;
  padding: 36px 28px 30px;
  position: relative;
  overflow: hidden;
}
/* Pattern de isotipos blancos como wallpaper sutil */
.neo-header::before {
  content: '';
  position: absolute; inset: 0;
  background-image: url('${t.pattern.isotipoBlanco}');
  background-repeat: repeat;
  background-size: 80px 80px;
  background-position: -16px -16px;
  opacity: 0.07;
  pointer-events: none;
}
/* Glow radial sutil arriba derecha */
.neo-header::after {
  content: '';
  position: absolute;
  right: -50px; top: -50px;
  width: 180px; height: 180px;
  border-radius: 50%;
  background: radial-gradient(circle, rgba(255,255,255,0.22) 0%, transparent 70%);
  pointer-events: none;
}
.neo-header img.neo-logo {
  height: 38px; width: auto;
  display: block;
  filter: brightness(0) invert(1);
  position: relative; z-index: 1;
  margin: 0 auto;
}
.neo-header .neo-eyebrow {
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 4px;
  margin-top: 16px;
  text-transform: uppercase;
  position: relative; z-index: 1;
  text-align: center;
  opacity: 0.85;
}
`.trim()
}

// ============================================================
// CSS para HERO ASOCIADO — overlapping card protagonista
// ============================================================

export function printableHeroCss(): string {
  const t = NEOCOOP_TOKENS
  return `
.neo-hero {
  position: relative;
  background: var(--surface);
  margin: -22px 24px 0;
  padding: 22px 22px 18px;
  border-radius: ${t.radius.inner};
  box-shadow: ${t.shadow.hero};
  z-index: 2;
  overflow: hidden;
}
/* Isotipo naranja como decoración inferior derecha */
.neo-hero::before {
  content: '';
  position: absolute; inset: 0;
  background-image: url('${t.pattern.isotipoNaranja}');
  background-repeat: no-repeat;
  background-position: right -28px bottom -28px;
  background-size: 130px 130px;
  opacity: 0.06;
  pointer-events: none;
}
.neo-hero .neo-hero-eyebrow {
  font-size: 9.5px;
  font-weight: 700;
  color: var(--muted);
  letter-spacing: 2px;
  text-transform: uppercase;
  position: relative; z-index: 1;
}
.neo-hero .neo-hero-headline {
  font-size: 24px;
  font-weight: 800;
  color: var(--ink);
  letter-spacing: -0.8px;
  line-height: 1.1;
  margin-top: 4px;
  position: relative; z-index: 1;
}
.neo-hero .neo-hero-ref {
  margin-top: 10px;
  display: inline-flex; align-items: center; gap: 8px;
  font-family: ${t.font.mono};
  font-size: 13px;
  font-weight: 600;
  letter-spacing: 1.4px;
  color: var(--brand);
  position: relative; z-index: 1;
  cursor: pointer;
  user-select: none;
  transition: opacity 120ms ease;
}
.neo-hero .neo-hero-ref:hover { opacity: 0.7; }
.neo-hero .neo-hero-ref .neo-copy-icon {
  width: 12px; height: 12px;
  opacity: 0.55;
  transition: opacity 120ms ease;
}
.neo-hero .neo-hero-ref:hover .neo-copy-icon { opacity: 1; }
.neo-hero .neo-hero-ref.copied { color: var(--ok-ink); }
.neo-hero .neo-hero-ref.copied .neo-copy-icon { display: none; }
.neo-hero .neo-hero-ref.copied::after {
  content: 'copiado ✓';
  font-size: 10px;
  color: var(--ok-ink);
  letter-spacing: 0.5px;
}
`.trim()
}

// ============================================================
// CSS para KEY-VALUE rows — la forma minimalista
// ============================================================

export function printableKvCss(): string {
  return `
.neo-kv {
  display: flex; justify-content: space-between; align-items: center;
  padding: 7px 0;
  font-size: 12px;
}
.neo-kv .k { color: var(--muted); font-weight: 500; }
.neo-kv .v {
  font-weight: 600; color: var(--ink); text-align: right;
  font-feature-settings: "tnum";
}
.neo-kv .v.mono { font-family: 'IBM Plex Mono', monospace; font-size: 11px; }
.neo-kv .v.copyable {
  cursor: pointer; user-select: none;
  transition: opacity 120ms ease;
}
.neo-kv .v.copyable:hover { opacity: 0.6; }
.neo-kv .v.copied { color: var(--ok-ink); }
.neo-kv .v.copied::after { content: ' ✓'; }

.neo-hr-dash {
  border: 0; border-top: 1px dashed var(--hairline);
  margin: 18px 0;
}
`.trim()
}

// ============================================================
// CSS para FOOTER LEGAL minimal
// ============================================================

export function printableLegalCss(): string {
  return `
.neo-legal {
  padding: 14px 28px 22px;
  text-align: center;
  font-size: 8.5px;
  color: var(--muted);
  line-height: 1.6;
  letter-spacing: 0.3px;
  border-top: 1px solid var(--hairline);
}
.neo-legal strong { color: var(--ink-soft); font-weight: 700; }
`.trim()
}

// ============================================================
// HELPERS HTML — fragmentos reutilizables
// ============================================================

/** Header brand sólido con logo grande + eyebrow opcional */
export function printableHeader(eyebrow?: string): string {
  const t = NEOCOOP_TOKENS
  return `
<div class="neo-header">
  <img class="neo-logo" src="${t.pattern.logoHorizontal}" alt="Neocoop" />
  ${eyebrow ? `<div class="neo-eyebrow">${escapeHtml(eyebrow)}</div>` : ''}
</div>
`.trim()
}

/**
 * Hero del asociado — protagonista. Click en `ref` copia al clipboard.
 *   eyebrow: 'Asociado'
 *   headline: 'Hugo Duarte'
 *   ref: 'NEO 482 619'
 *   refCopyValue: '482619'   (lo que va al clipboard al click)
 */
export function printableHero(opts: {
  eyebrow: string
  headline: string
  ref?: string
  refCopyValue?: string
}): string {
  const { eyebrow, headline, ref, refCopyValue } = opts
  return `
<div class="neo-hero">
  <div class="neo-hero-eyebrow">${escapeHtml(eyebrow)}</div>
  <div class="neo-hero-headline">${escapeHtml(headline)}</div>
  ${ref ? `
  <div class="neo-hero-ref" data-copy="${escapeHtml(refCopyValue ?? ref)}" title="Click para copiar">
    <span>${escapeHtml(ref)}</span>
    <svg class="neo-copy-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round">
      <rect x="9" y="9" width="13" height="13" rx="2"/>
      <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/>
    </svg>
  </div>` : ''}
</div>
`.trim()
}

/** Footer legal — 1 línea con NEOCOOP R.L. · NIT */
export function printableLegal(extra?: string): string {
  return `
<div class="neo-legal">
  <strong>NEOCOOP R.L.</strong> · NIT 120719290 ·
  ${extra ? `<br/>${escapeHtml(extra)}` : ''}
</div>
`.trim()
}

/** Script para activar copy-on-click en cualquier [data-copy] */
export function printableCopyScript(): string {
  return `
document.querySelectorAll('[data-copy]').forEach(function(el) {
  el.addEventListener('click', function() {
    var txt = el.getAttribute('data-copy');
    if (!txt) return;
    navigator.clipboard.writeText(txt).then(function() {
      el.classList.add('copied');
      setTimeout(function() { el.classList.remove('copied'); }, 1400);
    });
  });
});
`.trim()
}

// ============================================================
// Helper escapeHtml local (mismo pattern que en imprimibles.ts)
// ============================================================

function escapeHtml(s: string | null | undefined): string {
  if (!s) return ''
  return String(s)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;')
}
